import { Box, TextField, Typography } from "@mui/material";

import {
  CompareSide,
  SceneCompare,
  SceneLayerEntry,
} from "../../types/storyMap";
import { sideLayerEntries } from "./layerEntries";
import SceneLayerList from "./SceneLayerList";
import HelpTip from "./HelpTip";
import type { ScopeLists } from "./useScopeLists";
import type { StoryScopeInfo } from "./useStoryScope";

interface SideEditorProps {
  title: string;
  sideId: "before" | "after";
  side: CompareSide;
  onSideChange: (patch: Partial<CompareSide>) => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
}

/** Per-side editor: label + the shared "Add layer to map" list. */
function SideEditor({
  title,
  sideId,
  side,
  onSideChange,
  scope,
  lists,
}: SideEditorProps) {
  const entries = sideLayerEntries(side, sideId);
  const handleEntriesChange = (
    next: SceneLayerEntry[],
    opts?: { derived?: boolean }
  ) => {
    // Writing layerStack migrates a legacy side to the entry model. `derived`
    // (tile resolution) is not distinguished at the side level — the compare
    // patch path marks content dirty either way, matching the old behaviour.
    void opts;
    onSideChange({ layerStack: next });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
        {title}
      </Typography>
      <TextField
        label="Label"
        size="small"
        value={side.label}
        onChange={(e) => onSideChange({ label: e.target.value })}
      />
      <SceneLayerList entries={entries} onChange={handleEntriesChange} scope={scope} lists={lists} />
    </Box>
  );
}

interface CompareSceneSettingsProps {
  compare: SceneCompare;
  onChange: (patch: Partial<SceneCompare>) => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
}

/** Right-rail settings for scene mode "compare": both sides + guidance. */
export default function CompareSceneSettings({
  compare,
  onChange,
  scope,
  lists,
}: CompareSceneSettingsProps) {
  const patchSide =
    (key: "before" | "after") => (patch: Partial<CompareSide>) =>
      onChange({ [key]: { ...compare[key], ...patch } });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Before / after
        </Typography>
        <HelpTip title="Compare mode">
          <p>
            Two maps under one drag handle, locked to the same camera. Each side
            has its own layer list — add layers to either side, each with its
            own strategy, date or filters.
          </p>
          <p>
            The usual setup is the same date on both sides, with untreated
            layers on the left and your strategy's on the right — or the same
            strategy at two dates to show change over time.
          </p>
          <p>
            The <strong>Label</strong> is what the reader sees stamped on each
            half.
          </p>
        </HelpTip>
      </Box>

      <SideEditor
        title="Before"
        sideId="before"
        side={compare.before}
        onSideChange={patchSide("before")}
        scope={scope}
        lists={lists}
      />
      <SideEditor
        title="After"
        sideId="after"
        side={compare.after}
        onSideChange={patchSide("after")}
        scope={scope}
        lists={lists}
      />
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        The compare uses the scene camera. Readers drag the handle between the
        two live maps.
      </Typography>
    </Box>
  );
}
